import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import CategoryIcon from './CategoryIcon'

interface Category {
  id: string
  name: string
  icon?: string
}

interface CategorySelectProps {
  value: string
  onChange: (value: string) => void
  categories: Category[]
  placeholder?: string
  className?: string
}

export default function CategorySelect({ 
  value, 
  onChange, 
  categories, 
  placeholder = 'Categoria',
  className = ''
}: CategorySelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  
  // Sort categories alphabetically by name
  const sortedCategories = [...categories].sort((a, b) => 
    a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
  )
  
  const selectedCategory = categories.find(c => c.id === value)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-[44px] border border-gray-300 dark:border-slate-600 rounded-[12px] px-4 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white flex items-center justify-between gap-2 min-w-[120px] transition-all duration-200 ease-apple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 focus-visible:border-blue-500"
      >
        <span className="flex items-center gap-2 truncate">
          {selectedCategory ? (
            <>
              <CategoryIcon icon={selectedCategory.icon || ''} size="sm" />
              <span>{selectedCategory.name}</span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full bg-white dark:bg-slate-800 border border-gray-200/60 dark:border-slate-600/60 rounded-[12px] shadow-elevated max-h-60 overflow-auto">
          {/* Empty option */}
          <button
            type="button"
            onClick={() => {
              onChange('')
              setIsOpen(false)
            }}
            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-slate-700 ${
              !value ? 'bg-primary-50 dark:bg-primary-900/30' : ''
            }`}
          >
            <span className="w-4" />
            <span className="text-gray-500">{placeholder}</span>
            {!value && <Check className="w-4 h-4 ml-auto text-primary-600" />}
          </button>

          {/* Category options - sorted alphabetically */}
          {sortedCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                onChange(cat.id)
                setIsOpen(false)
              }}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-slate-700 ${
                value === cat.id ? 'bg-primary-50 dark:bg-primary-900/30' : ''
              }`}
            >
              <CategoryIcon icon={cat.icon || ''} size="sm" />
              <span className="text-gray-900 dark:text-white truncate">{cat.name}</span>
              {value === cat.id && <Check className="w-4 h-4 ml-auto text-primary-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
